import { memo } from 'react';
import * as Y from 'yjs';
import type { ConnectionLineComponentProps } from '@xyflow/react';
import { useCanvasDoc } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { useCanvasBinding } from "@/features/canvas/engine/wiring/contexts/canvas-binding-context";
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from "@/features/canvas/engine/wiring/canvas-roots";
import { useInFlightReconnectRef } from './in-flight-reconnect-context';
import {
  DEFAULT_EDGE_DATA,
  dashArrayFor,
  pathFor,
  type EdgeVisualData,
} from './edge-visuals';
import styles from './nula-edge.module.css';

/**
 * Reads the current visuals for the in-flight drag:
 *   - During reconnect: the edge being reconnected (so its user-edited
 *     stroke / width / style / curvature show on the rubber-band line).
 *   - During fresh-create: DEFAULT_EDGE_DATA (the same payload
 *     createEdge.ts seeds onto the new edge).
 *
 * Exported for unit testing — the rendering shell wires it through the
 * useCanvasDoc / useInFlightReconnectRef hooks below.
 */
export function readInFlightVisual(
  doc: Y.Doc | null,
  reconnectingId: string | null,
  binding: CanvasBinding = TOP_LEVEL_BINDING,
): EdgeVisualData {
  if (!doc || !reconnectingId) return DEFAULT_EDGE_DATA;
  const edge = canvasRoots(doc, binding).edges.get(reconnectingId);
  if (!edge) return DEFAULT_EDGE_DATA;
  const data = edge.get('data');
  if (!(data instanceof Y.Map)) return DEFAULT_EDGE_DATA;
  const merged: Record<string, unknown> = { ...DEFAULT_EDGE_DATA };
  data.forEach((v, k) => { merged[k] = v; });
  // Forward-fix legacy stroke token (mirrors useEdgesFromYjs.snapshotEdge).
  if (merged.stroke === 'var(--text-1)') merged.stroke = 'var(--on-surface)';
  return merged as EdgeVisualData;
}

/**
 * Custom connection line — replaces RF's default solid-blue rubber band.
 * Mirrors NulaEdge's path geometry (curvature) and stroke (color, width,
 * dash array) so the line the user drags looks exactly like the edge
 * that will be persisted on drop.
 *
 * For fresh-create drags (from a node handle to empty space), uses the
 * canvas's default edge style. For reconnect drags (from an existing
 * edge's endpoint), reads the in-flight edge's data via
 * InFlightReconnectContext and reflects its current customisation.
 */
export const NulaConnectionLine = memo(function NulaConnectionLine({
  fromX,
  fromY,
  toX,
  toY,
  fromPosition,
  toPosition,
}: ConnectionLineComponentProps) {
  const doc = useCanvasDoc();
  const binding = useCanvasBinding();
  const ref = useInFlightReconnectRef();
  const visual = readInFlightVisual(doc, ref?.current ?? null, binding);

  const [path] = pathFor(visual.curvature, {
    sourceX: fromX,
    sourceY: fromY,
    sourcePosition: fromPosition,
    targetX: toX,
    targetY: toY,
    targetPosition: toPosition,
  });

  const dashArray = dashArrayFor(visual.strokeStyle, visual.strokeWidth);

  return (
    <g>
      <path
        d={path}
        fill="none"
        stroke={visual.stroke}
        strokeWidth={visual.strokeWidth}
        strokeLinecap="round"
        {...(dashArray ? { strokeDasharray: dashArray } : {})}
      />
      {/* Endpoint circles — same r=5 as the selected-edge endpoints in
          NulaEdge so the in-flight rubber band visually matches the
          dragged edge. The follow-pointer endpoint is the second one. */}
      <circle cx={fromX} cy={fromY} r={5} className={styles.endpoint} />
      <circle cx={toX}   cy={toY}   r={5} className={styles.endpoint} />
    </g>
  );
});
